import { ToWords } from 'to-words';

const toWords = new ToWords({ localeCode: 'en-US' });
import { formatToCurrency } from '@/lib/helpers';

export const generatePromissoryNote = ({ loan, company, branch }) => {
  const todaysDate = new Date().toDateString();
  const companyName = (company.name || '').toUpperCase();
  const companyShortName = (company.shortName || '').toUpperCase();
  const totalLoan = formatToCurrency(loan.loanAmount || 0);
  const totalLoanInWords = toWords
    .convert(loan.loanAmount || 0, { currency: true, doNotAddOnly: true })
    .toUpperCase();
  const accountHolder = {
    fullName: `${loan.loanDistributions[0].person.firstName} ${
      loan.loanDistributions[0].person.middleName || ''
    } ${loan.loanDistributions[0].person.lastName}`.toUpperCase(),
    address: {
      street:
        loan.loanDistributions[0].person.livingAddress.street.toUpperCase(),
      city: loan.loanDistributions[0].person.livingAddress.city.toUpperCase(),
      province:
        loan.loanDistributions[0].person.livingAddress.province.toUpperCase(),
      country:
        loan.loanDistributions[0].person.livingAddress.country.toUpperCase()
    }
  };
  const requestedTenure = loan.tenure;
  const requestedTenureInWords = toWords
    .convert(loan.tenure || 0)
    .toUpperCase();
  const requestedInterest = loan.interest;
  const requestedInterestInWords = toWords
    .convert(loan.interest || 0)
    .toUpperCase();

  const config = {
    info: {
      title: 'Promissory-note', // name of the document being printed
      modDate: new Date(), // date the document was last modified
      creationDate: new Date(), // date the document was created
      keywords: 'promissory, promissory note, loan, finance', // keywords associated with the document
      author: 'Sodality Co-operative Credit Union Limited', // company name
      subject: 'Promissory Note', // name of the document being printed
      creator: 'John Doe', // name of the logged in user
      producer: 'Sodality Co-operative Credit Union Limited' // company name
    },

    defaultStyle: {
      font: 'Lora',
      color: '#0f172a',
      fontSize: 11,
      lineHeight: 1.3
    },

    content: [
      {
        text: `${companyName}`,
        alignment: 'center'
      },
      {
        text: 'PROMISSORY NOTE',
        alignment: 'center'
      },
      {
        text: `${todaysDate}`,
        margin: [0, 0, 0, 10]
      },
      {
        text: [
          '\tFOR VALUE RECEIVED, I/WE',
          {
            text: ` ${accountHolder.fullName} `,
            bold: true
          },
          `the undersigned of ${accountHolder.address.street}, ${accountHolder.address.city} in the parish of ${accountHolder.address.province} (Hereinafter referred to as “the Promisor”), DO HEREBY UNCONDITIONALLY PROMISES TO PAY to the order of the`,
          {
            text: ` ${companyName} `,
            bold: true
          },
          '(Hereinafter referred to as “',
          { text: ` ${companyShortName} `, bold: true },
          '” ) the principal sum of ',
          {
            text: `${totalLoanInWords} (${totalLoan}) `,
            bold: true
          },
          '(Hereinafter referred to as the “Loan”) by ',
          {
            text: `${requestedTenureInWords}(${requestedTenure}) `,
            bold: true
          },
          'equal consecutive monthly installments of ',
          {
            text: `${totalLoanInWords}(${totalLoan}) `,
            bold: true
          },
          'on each Repayment date'
        ],
        style: 'para',
        preserveLeadingSpaces: true,
        margin: [0, 0, 0, 10]
      },
      {
        text: [
          '\tThe Promisor further promises to pay interest on the Loan or the balance thereof outstanding from time to time from the date of the loan at the rate of',
          {
            text: ` ${requestedInterestInWords} (${requestedInterest}) `,
            bold: true
          },
          'percentage per annum (variable at the discretion of',
          { text: ` ${companyShortName} `, bold: true },
          '). Interest shall be deemed to accrue from day to day and shall, in respect of each Interest period, be calculated on the basis of the actual number of days elapsed divided by 365 and shall be due and payable on each Interest Payment Date.'
        ],
        margin: [0, 0, 0, 10]
      },
      {
        text: '\tPayment of principal and interest shall be made without set-off or counter-claim and free and clear of any present or future withholding or other taxes.',
        margin: [0, 0, 0, 10],
        preserveLeadingSpaces: true
      },
      {
        text: '\tSubject to the payment by the Promisor of any applicable prepayment fee due and payable under the credit agreement (Hereinafter referred to as the “Offer Letter”), the Promisor shall have the right to repay, in accordance with the terms of the Offer Letter, the whole or any part of the Loan.',
        margin: [0, 0, 0, 10],
        preserveLeadingSpaces: true
      },
      {
        text: '\tThe Promisor hereby irrevocably and unconditionally waives all requirements as to diligence, presentment, demand, protest, notice of dishonor and all other notices whatever, and agrees to pay all expenses including reasonable attorney’s fees incurred in the collection of this note.',
        margin: [0, 0, 0, 10],
        preserveLeadingSpaces: true
      },
      {
        text: '\tThe failure of the holder hereof to exercise any of its rights hereunder in any particular instance shall not constitute a waiver thereof in that or any other instance.',
        margin: [0, 0, 0, 10],
        preserveLeadingSpaces: true
      },
      {
        text: '\tThis Promissory Note is the Note referred to in, and is entitled to the benefits of the Offer Letter. The obligations of the Promisor to pay all amounts payable hereunder may be accelerated upon the occurrence of an Event of Default.',
        margin: [0, 0, 0, 10],
        preserveLeadingSpaces: true
      },
      {
        text: 'AS WITNESS the hand of the Promisor was affixed at',
        margin: [0, 0, 0, 10]
      },
      {
        text: [
          {
            text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
            decoration: 'underline'
          },
          'in the parish of',
          {
            text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
            decoration: 'underline'
          },
          'this',
          {
            text: 'ㅤㅤㅤㅤㅤ',
            decoration: 'underline'
          },
          'day of',
          {
            text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
            decoration: 'underline'
          },
          '20',
          {
            text: 'ㅤㅤㅤㅤ',
            decoration: 'underline'
          }
        ],
        margin: [0, 0, 0, 40]
      },
      {
        text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
        margin: [0, 0, 0, 10],
        decoration: 'underline'
      },
      {
        text: `Signature of Promisor -  ${accountHolder.fullName}`,
        margin: [0, 0, 0, 30],
        bold: true
      },
      {
        text: 'Witnessed By:',
        bold: true,
        margin: [0, 0, 0, 30]
      },
      {
        text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
        margin: [0, 0, 0, 0],
        decoration: 'underline'
      },
      {
        text: 'Name of WITNESS',
        margin: [0, 0, 0, 40]
      },
      {
        text: 'ㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤㅤ',
        margin: [0, 0, 0, 10],
        decoration: 'underline'
      },
      {
        text: 'Signature of WITNESS',
        margin: [0, 0, 0, 10]
      }
    ],

    footer: function (currentPage, pageCount) {
      return {
        text: `Page ${currentPage.toString()} of ${pageCount.toString()}`,
        alignment: 'center'
      };
    }
  };

  return config;
};
